import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ListsViewModel } from '@app/lists/model/lists-view.model';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { Order, OrderHeader } from '@usf/ngrx-order';
import { DeliveryDetails } from '@usf/order-types';
import { Subscription } from 'rxjs';
import { ListsDataForHomePage } from 'src/app/lists/model/lists-page.model';
import { OrderService } from 'src/app/order/services/order.service';
import { WMTService } from 'src/app/shared/services/wmt/wmt.service';
import { Swiper } from 'swiper';
import { environment } from '../../../../environments/environment';
import { TileTypes } from '../../../shared/constants/home-constants';
import { RecommendedContent, Tile } from '../../models/tile.model';
import { HomeService } from '../../services/home.service';
import { SiteCustomizationProfile } from '@usf/user-types/site-customization';
import { CustomizationOptions } from '@shared/constants/customizations.enum';
import { PATHS } from '../../../shared/constants/paths';
import { FEATURES } from '@shared/constants/splitio-features';

@Component({
  selector: 'app-tiles-container',
  templateUrl: './tiles-container.component.html',
  styleUrls: ['./tiles-container.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TilesContainerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() deliveryTime?: DeliveryDetails | Order;
  @Input() recommendedDetails?: RecommendedContent;
  @Input() listsData?: ListsDataForHomePage;
  @Input() newsPageData?: SiteCustomizationProfile;
  @Input() dealsFlag?: boolean;
  @Input() newsFlag?: boolean;
  @Input() betterBuysFlag: boolean;
  @Input() customerTimeZone: string;
  @Input() totalAnnualSavings: number;
  @Input() searchPath = PATHS.SEARCH;
  @ViewChild('homeSwiperRef') homeSwiperRef: any;

  tiles: Tile[] = [];
  wmtTile: Tile;
  recommendedTile: Tile;
  listsTile: Tile;
  reportTile: Tile;
  browseTile: Tile;
  dealsTile: Tile;
  newsTile: Tile;
  betterBuysTile: Tile;
  equipmentSuppliesTile: Tile;
  isMobile: boolean;
  loadSubmittedOrderSub$: Subscription = new Subscription();
  equipmentSuppliesSub$: Subscription = new Subscription();
  hideEtaGlobalFeatureFlag$: Subscription = new Subscription();
  hideEtaGlobalFeatureFlag: boolean;
  wmtTileOrder: Order;
  showEquipmentSupplies = false;

  constructor(
    readonly platformService: PlatformService,
    private translateService: TranslateService,
    private homeService: HomeService,
    private wmtService: WMTService,
    private orderService: OrderService,
    protected panAppState: PanAppState,
  ) {}

  setSwiperInstance(swiper: Swiper) {
    this.homeService.homeSwiperRef$.next(swiper);
  }

  ngOnInit(): void {
    this.equipmentSuppliesSub$ = this.panAppState
      .feature$([FEATURES.split_global_equipment_and_supplies.name])
      .subscribe(on => {
        this.showEquipmentSupplies = on;
        if (this.tiles.length > 0) {
          this.updateTiles();
        }
      });

    this.hideEtaGlobalFeatureFlag$ = this.panAppState
      .feature$([FEATURES.split_global_order_hide_eta.name])
      .subscribe(hideEtaFeatureFlag => {
        this.hideEtaGlobalFeatureFlag = hideEtaFeatureFlag;
        if (this.tiles.length > 0) {
          this.updateTiles();
        }
      });

    this.isMobile =
      this.platformService.platformType ===
      this.platformService.platformEnum.mobile;

    this.wmtTile = {
      type: TileTypes.wmt,
      id: 'home-wmt-tile',
      title: 'i18n.home.wmtTile.title',
      textButton: 'i18n.home.wmtTile.textButton',
      backgroundImage: "url('/assets/images/home_tile-bg_wmt.png')",
      path: [''],
      deliveryDate: '',
      deliveryTime: '',
      dtDescription: '',
      isDelivered: true,
      hideEtaRange: true,
      confirmedDeliveryDate: '',
      etaReasonShort: '',
      etaReasonLong: '',
    };
    this.recommendedTile = {
      type: TileTypes.recommended,
      id: 'home-recommended-tile',
      title: this.translateService.instant('i18n.home.recommendedTile.title', {
        value: '\u000A',
      }),
      textButton: 'i18n.home.recommendedTile.textButton',
      path: [''],
    };
    this.listsTile = {
      type: TileTypes.lists,
      id: 'home-lists-tile',
      title: 'i18n.home.listsTile.title',
      backgroundImage: "url('/assets/images/home_tile-bg_my-lists.png')",
      path: ['lists'],
      lists: [],
    };
    this.reportTile = {
      type: TileTypes.report,
      id: 'home-report-tile',
      title: 'i18n.home.reportTile.title',
      textBody: 'i18n.home.reportTile.textBody',
      textButton: 'i18n.home.reportTile.textButton',
      backgroundImage: "url('/assets/images/home-farmers-tile.png')",
      path: [`${environment?.farmersReportUrl}`],
    };
    this.browseTile = {
      type: TileTypes.browse,
      id: 'home-trending-tile',
      title: 'i18n.home.browseTile.title',
      textBody: 'i18n.home.browseTile.textBody',
      textButton: 'i18n.home.browseTile.textButton',
      backgroundImage: "url('/assets/images/home-browse-products-tile.png')",
      path: ['search/catalog'],
    };

    this.equipmentSuppliesTile = {
      type: TileTypes.equipmentSuppliesTile,
      id: 'home-equipment-tile',
      title: 'i18n.home.equipmentSupplies.title',
      textBody: 'i18n.home.equipmentSupplies.textBody',
      textButton: 'i18n.home.equipmentSupplies.textButton',
      backgroundImage: "url('/assets/images/home-equipment-supplies-tile.png')",
      path: [this.searchPath],
    };

    this.dealsTile = {
      type: TileTypes.deals,
      id: 'home-deals-tile',
      title: 'i18n.home.dealsTile.title',
      textBody: 'i18n.home.dealsTile.textBody',
      textButton: 'i18n.home.dealsTile.textButton',
      backgroundImage: "url('/assets/images/home-deals-tile.png')",
      path: [PATHS.DEALS],
    };
    this.newsTile = {
      type: TileTypes.news,
      id: 'home-news-tile',
      backgroundImage: "url('/assets/images/home-news-tile.png')",
    };
    this.betterBuysTile = {
      type: TileTypes.betterBuys,
      id: 'home-better-buys-tile',
      backgroundImage: "url('/assets/images/home-better-buys-tile.jpg')",
      path: [PATHS.BETTER_BUYS],
      title: 'i18n.home.betterBuysTile.title',
      textButton: 'i18n.home.betterBuysTile.textButton',
      textBody: 'i18n.home.betterBuysTile.textBody',
    };

    this.updateTiles();
  }

  ngOnChanges() {
    if (this.tiles.length > 0) {
      this.updateTiles();
    }
  }

  ngOnDestroy() {
    if (this.loadSubmittedOrderSub$) {
      this.loadSubmittedOrderSub$.unsubscribe();
    }
    if (this.hideEtaGlobalFeatureFlag$) {
      this.hideEtaGlobalFeatureFlag$.unsubscribe();
    }
    this.equipmentSuppliesSub$.unsubscribe();
  }

  updateTiles() {
    this.updateListsTile();
    this.updateWmtTile();
    this.updateRecommendedTile();
    this.updateDealsTile();
    this.updateNewsTile();
    this.updateBetterBuysTile();
    this.updateEquipmentSuppliesTile();
    this.tiles = [
      this.wmtTile,
      this.recommendedTile,
      this.listsTile,
      this.dealsTile,
      this.betterBuysTile,
      this.equipmentSuppliesTile,
      this.newsTile,
      this.reportTile,
      this.browseTile,
    ].filter(tile => !tile.hidden);
    this.homeSwiperRef?.nativeElement.swiper.update();
  }

  updateListsTile() {
    if (this.listsData?.isListDataLoaded) {
      const masterLists: ListsViewModel[] =
        this.listsData?.managedByUsfLists?.filter(
          list => list?.listKey?.listTypeId === 'ML',
        );
      this.listsTile.lists =
        this.listsData?.recentlyViewedLists?.length > 0
          ? [...this.listsData?.recentlyViewedLists?.slice(0, 2)]
          : masterLists?.length > 1
            ? [...masterLists?.slice(0, 2)]
            : masterLists.length === 1
              ? [
                  ...masterLists
                    .slice(0, 1)
                    .concat(this.listsData?.allLists?.slice(0, 1)),
                ]
              : this.listsData?.allLists?.length > 1
                ? [...this.listsData?.allLists?.slice(0, 2)]
                : this.listsData?.allLists?.length === 1
                  ? [...this.listsData?.allLists?.slice(0, 1)].concat([
                      ...this.listsData?.managedByUsfLists?.slice(0, 1),
                    ])
                  : this.listsData?.managedByUsfLists?.length > 0
                    ? [...this.listsData?.managedByUsfLists?.slice(0, 2)]
                    : [];

      if (this.listsTile.lists.length > 0) {
        this.listsTile.textBody = '';
        if (!this.isMobile) {
          this.listsTile.textButton = 'i18n.home.listsTile.viewAllLists';
        }
      } else {
        this.setNoLists();
      }
    }
  }

  setNoLists() {
    this.listsTile.lists = [];
    this.listsTile.textBody = 'i18n.home.listsTile.noLists';
    this.listsTile.textButton = !this.isMobile
      ? 'i18n.home.listsTile.createList'
      : 'i18n.home.listsTile.newList';
  }

  updateWmtTile() {
    if (!this.deliveryTime) {
      this.wmtTile.hidden = true;
    } else {
      if ('orderNumber' in this.deliveryTime) {
        let orderNumbers = this.deliveryTime?.orderNumber.toString().split('|');
        let allWillCallOrders = true;
        for (let i = 0; i < orderNumbers?.length; i++) {
          this.loadSubmittedOrder(orderNumbers[i]);
          if (this.wmtTileOrder?.orderHeader?.orderType !== 'WC') {
            allWillCallOrders = false;
            break;
          }
        }
        if (allWillCallOrders) {
          this.wmtTile.hidden = true;
        } else {
          this.wmtTile.hidden = false;
          this.updateDeliveryTime(this.wmtTileOrder, true);
          this.buildSubmittedOrderPath(
            this.wmtTileOrder,
            this.deliveryTime?.customerNumber,
          );
        }
      } else {
        if (
          this.deliveryTime?.orderHeader?.orderType === 'WC' &&
          !this.deliveryTime?.orderHeader?.deliveryEta
        ) {
          this.wmtTile.hidden = true;
        } else {
          this.wmtTile.hidden = false;
          this.updateDeliveryTime(this.deliveryTime, false);
          this.buildSubmittedOrderPath(
            this.deliveryTime,
            this.deliveryTime?.orderHeader.customerNumber,
          );
        }
      }
      const currentTileOrderHeader = (this.deliveryTime as Order)
        ?.orderHeader as OrderHeader;
      this.wmtTile.hideEtaRange =
        this.hideEtaGlobalFeatureFlag &&
        currentTileOrderHeader?.hideEta &&
        (currentTileOrderHeader?.orderType === 'RT' ||
          currentTileOrderHeader?.orderType === 'WC');

      this.wmtTile.confirmedDeliveryDate =
        currentTileOrderHeader?.confirmedDeliveryDate;
      this.wmtTile.etaReasonShort = currentTileOrderHeader?.etaReasonShort;
      this.wmtTile.etaReasonLong = currentTileOrderHeader?.etaReasonLong;
    }
  }

  updateNewsTile() {
    if (this.newsFlag && this.newsPageData) {
      this.newsTile.hidden = false;
      this.newsPageData?.properties?.forEach(customization => {
        if (customization?.name === CustomizationOptions.newsMenu) {
          this.newsTile.newsTitle = customization?.value;
          const newsName = customization.value?.split(' ')[0];
          this.newsTile.newsBody = `Get the latest news from ${newsName}`;
          this.newsTile.newsButton = `View ${customization?.value}`;
        }
        if (customization?.name === CustomizationOptions.newsMenuUrl) {
          this.newsTile.newsUrl = customization?.value;
        }
      });
    } else {
      this.newsTile.hidden = true;
    }
  }

  updateBetterBuysTile() {
    this.betterBuysTile.hidden = !this.betterBuysFlag;
    if (
      this.totalAnnualSavings > 0 &&
      this.platformService.platformType !==
        this.platformService.platformEnum.mobile
    ) {
      this.betterBuysTile.textBody = undefined;
    } else {
      this.betterBuysTile.textBody = 'i18n.home.betterBuysTile.textBody';
    }
  }

  loadSubmittedOrder(orderNumber: String) {
    this.loadSubmittedOrderSub$ = this.orderService
      ?.selectOrderWithTandemNumber(+orderNumber)
      ?.subscribe(order => {
        this.wmtTileOrder = order;
      });
  }

  updateDealsTile() {
    if (this.dealsFlag) {
      this.dealsTile.hidden = false;
    } else {
      this.dealsTile.hidden = true;
    }
  }

  updateEquipmentSuppliesTile() {
    if (this.showEquipmentSupplies) {
      this.equipmentSuppliesTile.hidden = false;
    } else {
      this.equipmentSuppliesTile.hidden = true;
    }
    this.equipmentSuppliesTile.path = [this.searchPath];
  }

  updateDeliveryTime(order?: Order, apiSource?: boolean) {
    this.wmtTile.dtDescription = this.isMobile
      ? 'i18n.home.wmtTile.etaMobile'
      : 'i18n.home.wmtTile.eta';
    if (order) {
      this.wmtTile.isDelivered = !!order?.orderHeader?.orderDelivered;
      let deliveryDate = this.wmtTile.isDelivered
        ? order?.orderHeader?.deliveredDtm
        : order?.orderHeader?.deliveryEta;
      if (apiSource) {
        if (
          order?.orderHeader?.deliveryEta ||
          order?.orderHeader?.deliveredDtm
        ) {
          // check descartes for delivery data
          if (!deliveryDate || this.deliveryDateHasPassed(deliveryDate)) {
            // check wmt for delivery data
            this.checkWmtServiceForDeliveryData();
          } else {
            this.formatWmtDeliveryData(deliveryDate, order);
          }
        } else {
          const dtApi: DeliveryDetails = this.deliveryTime as DeliveryDetails;
          if (dtApi?.actualDeliveryDate || dtApi?.estimatedDeliveryDate) {
            this.checkWmtServiceForDeliveryData();
          } else {
            this.wmtTile.deliveryTime = 'i18n.home.wmtTile.inTransit';
          }
        }
      } else {
        this.formatDeliveryData(deliveryDate, order);
      }
    } else {
      if (apiSource) {
        const dtApi: DeliveryDetails = this.deliveryTime as DeliveryDetails;
        this.wmtTile.isDelivered = !!dtApi?.isDelivered;
        if (dtApi?.actualDeliveryDate || dtApi?.estimatedDeliveryDate) {
          this.checkWmtServiceForDeliveryData();
        } else {
          this.wmtTile.deliveryTime = 'i18n.home.wmtTile.inTransit';
        }
      }
    }
  }

  checkWmtServiceForDeliveryData() {
    const dtApi: DeliveryDetails = this.deliveryTime as DeliveryDetails;
    let deliveryDate = this.wmtTile.isDelivered
      ? dtApi?.actualDeliveryDate
      : dtApi?.estimatedDeliveryDate;
    if (!deliveryDate || this.deliveryDateHasPassed(deliveryDate)) {
      this.wmtTile.hidden = true;
    } else {
      this.formatWmtDeliveryData(deliveryDate, null);
    }
  }

  formatWmtDeliveryData(deliveryDate: string | Date, order: Order) {
    if (this.wmtTile.isDelivered) {
      this.wmtTile.dtDescription = 'i18n.home.wmtTile.delivered';
    }
    this.wmtTile.deliveryDate = this.wmtService.getDeliveryDate(
      deliveryDate,
      this.wmtTile.isDelivered,
      this.isMobile,
    );
    if (order) {
      this.wmtTile.deliveryTime = this.wmtService.getDeliveryTime(
        order?.orderHeader?.deliveredDtm,
        order?.orderHeader?.etaRangeMinimum,
        order?.orderHeader?.etaRangeMaximum,
        this.wmtTile.isDelivered,
        false,
        this.customerTimeZone,
      );
    } else {
      this.wmtTile.deliveryTime = this.wmtService.getDeliveryTime(
        deliveryDate,
        null,
        null,
        this.wmtTile.isDelivered,
        true,
        this.customerTimeZone,
      );
    }
  }

  formatDeliveryData(deliveryDate: string | Date, order: Order) {
    if (this.wmtTile.isDelivered) {
      this.wmtTile.dtDescription = 'i18n.home.wmtTile.delivered';
    }
    this.wmtTile.deliveryDate = this.wmtService.getDeliveryDateWmtTile(
      deliveryDate,
      this.wmtTile.isDelivered,
      this.isMobile,
      this.customerTimeZone,
    );
    if (order) {
      this.wmtTile.deliveryTime = this.wmtService.getDeliveryTime(
        order?.orderHeader?.deliveredDtm,
        order?.orderHeader?.etaRangeMinimum,
        order?.orderHeader?.etaRangeMaximum,
        this.wmtTile.isDelivered,
        false,
        this.customerTimeZone,
      );
    } else {
      this.wmtTile.deliveryTime = this.wmtService.getDeliveryTime(
        deliveryDate,
        null,
        null,
        this.wmtTile.isDelivered,
        true,
        this.customerTimeZone,
      );
    }
  }

  deliveryDateHasPassed(deliveryDate: any): boolean {
    const today = new Date().toISOString().substring(0, 10);
    const compareDeliveryDate = deliveryDate.toString().substring(0, 10);

    const todayParsed = Date.parse(today);
    const compareDeliveryDateParsed = Date.parse(compareDeliveryDate);

    return compareDeliveryDateParsed < todayParsed;
  }

  buildSubmittedOrderPath(order?: Order, customerNumber?: number) {
    if (order) {
      const orderInfo =
        `${customerNumber}:` +
        `${order.orderHeader.departmentNumber}:` +
        `${order.orderHeader.orderId}`;
      this.wmtTile.path = ['order', 'submitted-order', orderInfo];
    } else {
      this.wmtTile.path = ['my-orders'];
    }
  }

  updateRecommendedTile() {
    if (!this.recommendedDetails) {
      this.recommendedTile.hidden = true;
    } else {
      this.recommendedTile.hidden = false;
      this.recommendedTile.recommendedContent = { ...this.recommendedDetails };
    }
  }
}
